import {
    InputAdornment,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { useNavigate, useParams } from "react-router-dom";
import Image1 from "../../images/questions/image1.png";
import SnackbarAlert from "../../components/SnackBar";
import { buildQuestionFormData } from "../../helper/QuestionFormData";
import Fetch from "../../services/Fetch";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import QuizIcon from "@mui/icons-material/Quiz";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";

function CreateQuestion() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [exam, setExam] = useState(null);
    const [textEn, setTextEn] = useState("");
    const [textAr, setTextAr] = useState("");
    const [questionType, setQuestionType] = useState("");
    const [correctAnswer, setCorrectAnswer] = useState("true");
    const [fullMark, setFullMark] = useState("1");
    const param = useParams();
    const navigate = useNavigate();

    const questionsPath = `/courses/${param.course_id}/exams/${param.exam_id}/questions`;

    const examName = useMemo(() => {
        if (!exam) return "";
        return isAr ? exam.name_ar : exam.name_en;
    }, [exam, isAr]);

    const loadExam = async () => {
        setGetWait(true);
        const result = await Fetch(
            `${host}/courses/${param.course_id}/exams/${param.exam_id}/show`,
            "GET",
            null
        );
        if (result.status === 200) {
            setExam(result.data.data);
        }
        setGetWait(false);
    };

    useEffect(() => {
        loadExam();
    }, [param.course_id, param.exam_id]);

    const createQuestion = async () => {
        if (!textEn.trim() || !textAr.trim()) {
            setSnackBar("error", isAr ? "أدخل نص السؤال بالعربية والإنجليزية" : "Enter question text in Arabic and English");
            return;
        }
        if (!questionType) {
            setSnackBar("error", isAr ? "اختر نوع السؤال" : "Select question type");
            return;
        }
        if (!fullMark || Number(fullMark) <= 0) {
            setSnackBar("error", isAr ? "أدخل درجة السؤال" : "Enter question mark");
            return;
        }
        if (questionType === "true_false" && !correctAnswer) {
            setSnackBar("error", isAr ? "حدد الإجابة الصحيحة (صح أو خطأ)" : "Select the correct answer (true or false)");
            return;
        }

        setSendWait(true);

        const formData = buildQuestionFormData({
            textEn: textEn.trim(),
            textAr: textAr.trim(),
            questionType,
            fullMark,
            examId: param.exam_id,
            correctAnswer: questionType === "true_false" ? correctAnswer === "true" : undefined,
        });

        const result = await Fetch(`${host}/teacher/courses/exams/questions/store`, "POST", formData);

        if (result.status === 200) {
            setSnackBar("success", isAr ? "تم إضافة السؤال وحفظه" : "Question added and saved");
            navigate(questionsPath);
        } else if (result.status === 422) {
            setSnackBar("error", result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar("error", isAr ? "فشل إضافة السؤال" : "Failed to add question");
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    const questionTypes = [
        { value: "multiple_options", labelEn: "Multiple choice", labelAr: "اختيار من متعدد" },
        { value: "true_false", labelEn: "True or false", labelAr: "صح أو خطأ" },
    ];

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "إضافة سؤال" : "Add question"}
                    subtitle={isAr ? "أضف سؤالاً جديداً للاختبار" : "Add a new question to the exam"}
                    icon={<QuizIcon />}
                    actions={
                        <>
                            <Button
                                variant="contained"
                                startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                onClick={createQuestion}
                                disabled={sendWait}
                                sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                            >
                                {isAr ? "إضافة سؤال" : "Add question"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(questionsPath)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للأسئلة" : "Back to questions"}
                            </Button>
                        </>
                    }
                />

                {exam ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    bgcolor: "#eff6ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <AssignmentIcon sx={{ color: "#0D358C", fontSize: 32 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الاختبار المختار" : "Selected exam"}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                    {examName}
                                </Typography>
                                <Chip size="small" label={`ID #${exam.id}`} sx={{ mt: 0.5 }} />
                            </Box>
                        </Box>
                    </ScrollReveal>
                ) : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        direction: "ltr",
                        gap: 3,
                        alignItems: "stretch",
                    }}
                >
                    <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                        <Box
                            sx={{
                                height: "100%",
                                minHeight: { lg: 400 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                p: 4,
                                borderRadius: 4,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                background: theme.palette.mode === "dark"
                                    ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                    : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                            }}
                        >
                            <Box
                                component="img"
                                src={Image1}
                                alt=""
                                sx={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 280, lineHeight: 1.7 }}>
                                {questionType === "true_false"
                                    ? (isAr
                                        ? "سؤال صح أو خطأ يُنشئ تلقائياً خيارين: صح وخطأ — حدد الإجابة الصحيحة فقط"
                                        : "True/false questions auto-create True and False options — just pick the correct answer")
                                    : (isAr
                                        ? "بعد إضافة السؤال يمكنك إضافة خيارات الإجابة من قائمة الأسئلة"
                                        : "After adding the question, add answer options from the questions list")}
                            </Typography>
                        </Box>
                    </ScrollReveal>

                    <Box sx={{ flex: 1, minWidth: 0, direction: isAr ? "rtl" : "ltr" }}>
                        <DashboardPanelCard
                            title={isAr ? "بيانات السؤال" : "Question details"}
                            subtitle={isAr ? "يُحفظ في قاعدة البيانات ويرتبط بالاختبار" : "Saved to the database and linked to the exam"}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    sx={fieldSx}
                                    value={textEn}
                                    onChange={(e) => setTextEn(e.target.value)}
                                    label={isAr ? "نص السؤال بالإنجليزية" : "Question text (English)"}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    minRows={3}
                                    required
                                />
                                <TextField
                                    sx={fieldSx}
                                    value={textAr}
                                    onChange={(e) => setTextAr(e.target.value)}
                                    label={isAr ? "نص السؤال بالعربية" : "Question text (Arabic)"}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    minRows={3}
                                    required
                                />

                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel id="question-type-label">
                                            {isAr ? "نوع السؤال" : "Question type"}
                                        </InputLabel>
                                        <Select
                                            labelId="question-type-label"
                                            value={questionType}
                                            label={isAr ? "نوع السؤال" : "Question type"}
                                            onChange={(e) => setQuestionType(e.target.value)}
                                        >
                                            {questionTypes.map((item) => (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {isAr ? item.labelAr : item.labelEn}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        sx={fieldSx}
                                        type="number"
                                        value={fullMark}
                                        onChange={(e) => setFullMark(e.target.value)}
                                        label={isAr ? "درجة السؤال" : "Question mark"}
                                        variant="outlined"
                                        size="small"
                                        required
                                        inputProps={{ min: 0.5, step: 0.5 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <GradeOutlinedIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {questionType === "true_false" ? (
                                    <FormControl component="fieldset" sx={{ mt: 0.5 }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#0D358C" }}>
                                            {isAr ? "الإجابة الصحيحة" : "Correct answer"}
                                        </Typography>
                                        <RadioGroup
                                            row
                                            value={correctAnswer}
                                            onChange={(e) => setCorrectAnswer(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="true"
                                                control={<Radio color="primary" />}
                                                label={isAr ? "صح" : "True"}
                                            />
                                            <FormControlLabel
                                                value="false"
                                                control={<Radio color="primary" />}
                                                label={isAr ? "خطأ" : "False"}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                ) : null}

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                        justifyContent: "flex-end",
                                        mt: 2,
                                        pt: 2,
                                        borderTop: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                    }}
                                >
                                    <Button variant="outlined" onClick={() => navigate(questionsPath)} disabled={sendWait}>
                                        {isAr ? "إلغاء" : "Cancel"}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={createQuestion}
                                        disabled={sendWait}
                                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                        sx={{
                                            minWidth: 200,
                                            px: 3,
                                            bgcolor: "#0D358C",
                                            fontWeight: 700,
                                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                                            "&:hover": { bgcolor: "#1d4ed8" },
                                        }}
                                    >
                                        {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "إضافة سؤال" : "Add question")}
                                    </Button>
                                </Box>
                            </Box>
                        </DashboardPanelCard>
                    </Box>
                </Box>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default CreateQuestion;
